import { useCallback, useRef, useState } from "react";
import { MoveHorizontal, Camera } from "lucide-react";

const Side = ({ label, corner, tint }) => (
  <div className="absolute inset-0 flex items-center justify-center" style={{ background: tint }}>
    <div
      className="absolute inset-0"
      style={{
        background:
          "repeating-linear-gradient(45deg, rgba(255,255,255,0.02) 0px, rgba(255,255,255,0.02) 1px, transparent 1px, transparent 9px)",
      }}
    />
    <Camera size={30} strokeWidth={1.5} style={{ color: "rgba(201,162,75,0.55)", position: "relative" }} />
    <span
      className={`absolute top-3 ${corner === "left" ? "left-3" : "right-3"} uppercase`}
      style={{
        background: "var(--charcoal)",
        color: "var(--text-light)",
        fontSize: "11px",
        letterSpacing: "0.2em",
        padding: "5px 10px",
        borderRadius: "4px",
        border: "1px solid var(--border-dark)",
      }}
    >
      {label}
    </span>
  </div>
);

export const BeforeAfterSlider = ({ caption, beforeSrc, afterSrc, beforeAlt, afterAlt, "data-testid": testId }) => {
  const [pos, setPos] = useState(50);
  const containerRef = useRef(null);
  const dragging = useRef(false);

  const update = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(2, Math.min(98, p)));
  }, []);

  const onMove = useCallback(
    (e) => {
      if (!dragging.current) return;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      update(clientX);
    },
    [update]
  );

  const stop = useCallback(() => {
    dragging.current = false;
    window.removeEventListener("mousemove", onMove);
    window.removeEventListener("mouseup", stop);
    window.removeEventListener("touchmove", onMove);
    window.removeEventListener("touchend", stop);
  }, [onMove]);

  const start = useCallback(
    (e) => {
      dragging.current = true;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      update(clientX);
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", stop);
      window.addEventListener("touchmove", onMove, { passive: true });
      window.addEventListener("touchend", stop);
    },
    [onMove, stop, update]
  );

  const CornerLabel = ({ label, corner }) => (
    <span
      className={`absolute top-3 ${corner === "left" ? "left-3" : "right-3"} uppercase`}
      style={{ background: "rgba(13,13,15,0.78)", color: "var(--text-light)", fontSize: "11px", letterSpacing: "0.2em", padding: "5px 10px", borderRadius: "4px", border: "1px solid rgba(255,255,255,0.14)", zIndex: 5 }}
    >
      {label}
    </span>
  );

  return (
    <div data-testid={testId}>
      <div
        ref={containerRef}
        className="relative ratio-16-10 select-none overflow-hidden"
        style={{ borderRadius: "8px", cursor: "ew-resize" }}
        onMouseDown={start}
        onTouchStart={start}
      >
        {/* AFTER (base) */}
        {afterSrc ? (
          <img src={afterSrc} alt={afterAlt || "After"} draggable="false" className="absolute inset-0" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <Side label="AFTER" corner="right" tint="#20201d" />
        )}
        <CornerLabel label="AFTER" corner="right" />
        {/* BEFORE (clipped) */}
        <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
          {beforeSrc ? (
            <img src={beforeSrc} alt={beforeAlt || "Before"} draggable="false" className="absolute inset-0" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            <Side label="BEFORE" corner="left" tint="#131316" />
          )}
          <CornerLabel label="BEFORE" corner="left" />
        </div>
        {/* divider + handle */}
        <div
          className="absolute top-0 bottom-0"
          style={{ left: `${pos}%`, transform: "translateX(-50%)", width: "2px", background: "var(--gold)", zIndex: 6 }}
        >
          <div
            className="absolute top-1/2 left-1/2 flex items-center justify-center"
            style={{
              width: "44px",
              height: "44px",
              transform: "translate(-50%, -50%)",
              borderRadius: "999px",
              background: "var(--gold-gradient)",
              boxShadow: "0 4px 14px rgba(0,0,0,0.35)",
            }}
          >
            <MoveHorizontal size={20} strokeWidth={2} style={{ color: "var(--black)" }} />
          </div>
        </div>
      </div>
      {caption ? (
        <p className="mt-3 text-muted-warm" style={{ fontSize: "14px" }}>
          {caption}
        </p>
      ) : null}
    </div>
  );
};
